import {
  CAMPO_EM_BRANCO, escapeHtml, formatarCep, formatarCpfCnpj, formatarMoeda, municipioUf, rotuloDocumento, valorPorExtenso
} from '../telas/Procuracao';

// Modelos dos documentos complementares (declarações e requerimentos de vistoria/DETRAN).
// Cada modelo descreve seus campos extras e monta o HTML do documento a partir dos dados do
// cliente e do veículo cadastrados. O HTML sai no mesmo formato da Procuração (folha A4 editável).

const e = escapeHtml;
const preenchido = (v) => v != null && String(v).trim() !== '';
const texto = (v) => (preenchido(v) ? String(v).trim() : '');
const negrito = (v) => `<strong>${e(preenchido(v) ? texto(v) : CAMPO_EM_BRANCO)}</strong>`;
// Campo de formulário: valor preenchido ou uma linha em branco para completar à mão
const valorOuLinha = (v) => (preenchido(v) ? e(texto(v)) : '<span class="branco"></span>');

const MESES = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];

// "2026-03-21" → { dia: '21', mes: '03', ano: '2026' }
function partesData(iso) {
  if (!iso) return null;
  const [ano, mes, dia] = iso.split('-');
  return ano && mes && dia ? { dia, mes, ano } : null;
}

export function dataPorExtenso(iso) {
  const p = partesData(iso);
  return p ? `${Number(p.dia)} de ${MESES[Number(p.mes) - 1]} de ${p.ano}` : '____ de ________________ de 20____';
}

export function dataCurta(iso) {
  const p = partesData(iso);
  return p ? `${p.dia}/${p.mes}/${p.ano}` : '____/____/20____';
}

// ── Trechos reutilizados ─────────────────────────────────────────────────────

// "Rua X, Nº10, Casa 2, bairro Centro, CEP 96600-000, Canguçu-RS" — só o que estiver preenchido
export function enderecoCompleto(p) {
  const rua = p.logradouro ? `${p.logradouro}${p.numero ? `, Nº${p.numero}` : ''}` : (p.numero ? `Nº${p.numero}` : '');
  const cep = formatarCep(p.cep);
  const partes = [rua, p.complemento, p.bairro && `bairro ${p.bairro}`, cep && `CEP ${cep}`, municipioUf(p)]
    .map(texto).filter(Boolean);
  return partes.length ? partes.join(', ') : CAMPO_EM_BRANCO;
}

// "portador(a) do RG nº X e do CPF nº Y" (sem o RG quando não cadastrado)
function portador(p) {
  const doc = `${rotuloDocumento(p.cpfCnpj)} nº ${negrito(formatarCpfCnpj(p.cpfCnpj))}`;
  return preenchido(p.rg) ? `portador(a) do RG nº ${negrito(p.rg)} e do ${doc}` : `portador(a) do ${doc}`;
}

// "Eu, NOME, portador(a) do ..., residente e domiciliado(a) em ENDEREÇO"
function qualificacao(p, { nacionalidade = '' } = {}) {
  const nac = preenchido(nacionalidade) ? `, ${e(texto(nacionalidade))}` : '';
  return `Eu, ${negrito(p.nome)}${nac}, ${portador(p)}, residente e domiciliado(a) em ${e(enderecoCompleto(p))}`;
}

// "Fiat Uno, cor branca, ano 2010, de placa ABC1D23, chassi ..., RENAVAM ..."
function veiculoFrase(v, { numeroMotor = '' } = {}) {
  const partes = [negrito(v.marcaModelo)];
  if (preenchido(v.cor)) partes.push(`cor ${e(texto(v.cor))}`);
  if (preenchido(v.ano)) partes.push(`ano ${e(texto(v.ano))}`);
  partes.push(`de placa ${negrito((v.placa || '').toUpperCase())}`);
  partes.push(`chassi ${negrito((v.chassi || '').toUpperCase())}`);
  if (preenchido(v.renavam)) partes.push(`RENAVAM ${e(texto(v.renavam))}`);
  if (preenchido(numeroMotor)) partes.push(`motor nº ${e(texto(numeroMotor))}`);
  return partes.join(', ');
}

// Dados do veículo um embaixo do outro, como no modelo da Procuração
function veiculoLista(v, rotulos, { numeroMotor = '' } = {}) {
  const linhas = [];
  const linha = (rotulo, valor, opcional) => {
    if (opcional && !preenchido(valor)) return;
    linhas.push(`<div><strong>${rotulo}:</strong> ${e(preenchido(valor) ? texto(valor) : CAMPO_EM_BRANCO)}</div>`);
  };
  rotulos.forEach((r) => {
    if (r === 'PLACA') linha('PLACA', (v.placa || '').toUpperCase());
    if (r === 'CHASSI') linha('CHASSI', (v.chassi || '').toUpperCase());
    if (r === 'RENAVAM') linha('RENAVAM', v.renavam, true);
    if (r === 'MODELO') linha('MODELO', v.marcaModelo);
    if (r === 'COR') linha('COR', v.cor, true);
    if (r === 'ANO') linha('ANO', v.ano, true);
    if (r === 'MOTOR') linha('Nº DO MOTOR', numeroMotor);
  });
  return `<div class="veiculo">\n${linhas.join('\n')}\n</div>`;
}

const titulo = (t, longo) => `<h1${longo ? ' class="longo"' : ''}>${t}</h1>`;
const par = (html) => `<p class="par">${html}</p>`;
const opcao = (marcado, rotulo) => `<div class="opcao">(&nbsp;${marcado ? 'X' : '&nbsp;&nbsp;'}&nbsp;) ${e(rotulo)}</div>`;

function localData(ctx) {
  return `${e(texto(ctx.local) || '______________________')}, ${dataPorExtenso(ctx.dataISO)}`;
}

// Data (à esquerda do traço) e legenda da assinatura, no padrão da Procuração
function fechamento(dataHtml, legenda, nota = '') {
  return `<div class="fecha">
<p class="data">${dataHtml}</p>
<div class="linha"></div>
<span>${legenda}</span>${nota ? `\n<p class="nota">${nota}</p>` : ''}
</div>`;
}

// ── Modelos ──────────────────────────────────────────────────────────────────

// Declarações de "não apresentação" de etiqueta/plaqueta/tampa: mesmo texto, muda o item e o motivo.
function declaracaoNaoApresentacao({ id, titulo: nome, sub, objeto, motivo }) {
  return {
    id,
    grupo: 'Declarações',
    titulo: nome,
    sub,
    campos: [],
    gerar: (ctx) => [
      titulo('DECLARAÇÃO'),
      par(`${qualificacao(ctx.declarante)}, declaro ficar civil e criminalmente responsável pela não apresentação ${objeto} do veículo ${veiculoFrase(ctx.veiculo)}, ${motivo}.`),
      fechamento(localData(ctx), 'Declarante')
    ].join('\n')
  };
}

const MOTIVO_EXTRAVIO = 'sendo que a mesma foi extraviada';

const campoMotor = { chave: 'numeroMotor', rotulo: 'Nº do motor', tipo: 'texto', placeholder: 'Número gravado no motor' };
const campoNacionalidade = { chave: 'nacionalidade', rotulo: 'Nacionalidade (aparece no documento)', tipo: 'texto', padrao: 'brasileiro' };
const rotulosVeiculo = (comMotor) => ['PLACA', 'CHASSI', 'RENAVAM', 'MODELO', ...(comMotor ? ['MOTOR'] : [])];

// Morfologia: o número do chassi/motor não pode ser conferido com a última vistoria. O VENDEDOR
// assume a responsabilidade desde a data em que comprou o veículo até a data em que o vendeu.
function declaracaoMorfologia({ id, titulo: nome, sub, alvo, comMotor }) {
  return {
    id,
    grupo: 'Declarações',
    titulo: nome,
    sub,
    rotuloDeclarante: 'Vendedor (declarante)',
    campos: [
      { chave: 'dataCompra', rotulo: 'Data em que o vendedor comprou o veículo', tipo: 'data' },
      { chave: 'dataVenda', rotulo: 'Data em que o vendedor vendeu o veículo', tipo: 'data' },
      campoNacionalidade,
      ...(comMotor ? [campoMotor] : [])
    ],
    gerar: (ctx) => [
      titulo('DECLARAÇÃO'),
      par(`${qualificacao(ctx.declarante, { nacionalidade: ctx.extras.nacionalidade })}, venho por meio deste declarar que fico civil, criminalmente e administrativamente responsável pela morfologia do número do ${alvo} do veículo abaixo identificado, tendo em vista a impossibilidade de conferência com a última vistoria, desde a data em que o adquiri, ${negrito(ctx.extras.dataCompra ? dataCurta(ctx.extras.dataCompra) : '')}, até a data em que o vendi, ${negrito(ctx.extras.dataVenda ? dataCurta(ctx.extras.dataVenda) : '')}.`),
      veiculoLista(ctx.veiculo, rotulosVeiculo(comMotor), { numeroMotor: ctx.extras.numeroMotor }),
      fechamento(localData(ctx), '(RECONHECER FIRMA VERDADEIRA)')
    ].join('\n')
  };
}

// Remarcação: o número do chassi/motor precisa ser remarcado. Quem assina é o ADQUIRENTE,
// responsável pela remarcação do veículo que está adquirindo.
function declaracaoRemarcacao({ id, titulo: nome, sub, alvo, comMotor }) {
  return {
    id,
    grupo: 'Declarações',
    titulo: nome,
    sub,
    rotuloDeclarante: 'Adquirente (declarante)',
    campos: [campoNacionalidade, ...(comMotor ? [campoMotor] : [])],
    gerar: (ctx) => [
      titulo('DECLARAÇÃO'),
      par(`${qualificacao(ctx.declarante, { nacionalidade: ctx.extras.nacionalidade })}, venho por meio deste declarar que fico civil, criminalmente e administrativamente responsável pela remarcação do número do ${alvo} do veículo que estou adquirindo.`),
      veiculoLista(ctx.veiculo, rotulosVeiculo(comMotor), { numeroMotor: ctx.extras.numeroMotor }),
      fechamento(localData(ctx), '(RECONHECER FIRMA VERDADEIRA)')
    ].join('\n')
  };
}

const MOTIVOS_BAIXA = [
  { valor: 'IRRECUPERAVEL', rotulo: 'Veículo irrecuperável' },
  { valor: 'DESMONTADO', rotulo: 'Veículo definitivamente desmontado' },
  { valor: 'PERDA_TOTAL', rotulo: 'Veículo sinistrado com laudo de perda total' },
  { valor: 'SUCATA', rotulo: 'Veículo vendido ou leiloado como sucata' },
  { valor: 'OUTRO_PAIS', rotulo: 'Baixa de Veículo para outro País conforme decalque ou fotografia da gravação do número do chassi, em anexo.' }
];

const PERDIDOS_BAIXA = [
  { valor: 'CRV', rotulo: 'CRV (se veículo verde)' },
  { valor: 'PLACAS', rotulo: 'Placas' },
  { valor: 'PLAQUETAS', rotulo: 'Plaquetas' },
  { valor: 'LACRE', rotulo: 'Lacre' }
];

const JUSTIFICATIVAS_CRV = [
  { valor: 'NAO_RECEBIDO', rotulo: 'não recebido;' },
  { valor: 'INUTILIZADO', rotulo: 'inutilizado (deverá acompanhar o documento no estado em que se encontra);' },
  { valor: 'EXTRAVIADO', rotulo: 'perdido/extraviado (deverá acompanhar Boletim de Ocorrência Policial do fato);' },
  { valor: 'FURTADO', rotulo: 'furtado/roubado (deverá acompanhar Boletim de Ocorrência Policial do fato).' }
];

// Motivo único (select) com a opção de deixar em branco para marcar à mão
const EM_BRANCO = { valor: '', rotulo: 'Deixar em branco (marcar à mão)' };
const campoEscolha = (chave, rotulo, opcoes) => ({ chave, rotulo, tipo: 'select', opcoes: [EM_BRANCO, ...opcoes] });

// ── Recibos de compra e venda de motor / carroceria ──────────────────────────
// O veículo do documento é o que RECEBE a peça; o de onde ela saiu vem em ctx.veiculoOrigem (quadro próprio na
// tela, com importação do CRLV ou busca no cadastro).

const campoValorRecibo = { chave: 'valor', rotulo: 'Valor da venda', tipo: 'valor' };

// Tipo da carroceria escrito à mão (obrigatório: a tela não gera o documento sem ele)
const camposCarroceria = [
  { chave: 'tipoCarroceria', rotulo: 'Tipo de carroceria', tipo: 'texto', obrigatorio: true, placeholder: 'Ex.: Carga seca, Baú, Boiadeira (VTAV), Prancha...' },
  { chave: 'fabricanteCarroceria', rotulo: 'Marca/fabricante da carroceria (opcional)', tipo: 'texto' },
  { chave: 'anoCarroceria', rotulo: 'Ano de fabricação da carroceria (opcional)', tipo: 'texto' }
];

// "carroceria tipo PRANCHA, marca/fabricante X, ano de fabricação Y" (só o que estiver preenchido)
function descricaoCarroceria(extras) {
  return `carroceria tipo ${negrito(extras.tipoCarroceria)}${
    preenchido(extras.fabricanteCarroceria) ? `, marca/fabricante ${negrito(extras.fabricanteCarroceria)}` : ''}${
    preenchido(extras.anoCarroceria) ? `, ano de fabricação ${negrito(extras.anoCarroceria)}` : ''}`;
}

// Data em que o CAT passou a existir (Portaria DENATRAN nº 27, de 07/05/2002): carroceria fabricada antes
// disso não tem CAT/CCT, e a procedência é comprovada por declaração do proprietário
const ANTERIOR_CAT = 'ANTERIOR_07_05_2002';
const DISPENSA_CAT = 'foi fabricada anteriormente a 07 de maio de 2002, data da Portaria DENATRAN nº 27/2002, que instituiu o Certificado de Adequação à Legislação de Trânsito – CAT, razão pela qual não possui CAT nem Certificado de Capacitação Técnica – CCT, ficando dispensada a sua apresentação';

// "a importância de R$ 1.500,00 (mil e quinhentos reais)" ou linhas para preencher à mão
function valorComExtenso(valor) {
  if (!preenchido(valor)) return 'R$ <span class="branco"></span> (<span class="branco"></span>)';
  return `<strong>${e(formatarMoeda(valor))} (${e(valorPorExtenso(valor))})</strong>`;
}

// "Eu, VENDEDOR, ..., declaro que recebi de COMPRADOR, CPF nº ..., residente em ..., a importância de ..."
function recebimento(ctx) {
  const c = ctx.comprador;
  return `${qualificacao(ctx.declarante)}, declaro que recebi de ${negrito(c.nome)}, ${rotuloDocumento(c.cpfCnpj)} nº ${negrito(formatarCpfCnpj(c.cpfCnpj))}, residente e domiciliado(a) em ${e(enderecoCompleto(c))}, a importância de ${valorComExtenso(ctx.extras.valor)}`;
}

const PROCEDENCIA_RECIBO = 'Declaro, ainda, ser o legítimo proprietário do bem ora vendido, que o mesmo possui procedência lícita e encontra-se livre e desembaraçado de quaisquer ônus, responsabilizando-me civil e criminalmente pelas informações aqui prestadas, e dou ao comprador plena, geral e irrevogável quitação do valor recebido.';

export const MODELOS = [
  // ── Declarações ───────────────────────────────────────────────────────────
  declaracaoMorfologia({
    id: 'morfologia-chassi',
    titulo: 'Responsabilidade pela morfologia do chassi',
    sub: 'Vendedor: chassi sem conferência com a última vistoria, da compra até a venda',
    alvo: 'chassi',
    comMotor: false
  }),
  declaracaoMorfologia({
    id: 'morfologia-motor',
    titulo: 'Responsabilidade pela morfologia do motor',
    sub: 'Vendedor: motor sem conferência com a última vistoria, da compra até a venda',
    alvo: 'motor',
    comMotor: true
  }),
  declaracaoRemarcacao({
    id: 'remarcacao-chassi',
    titulo: 'Responsabilidade pela remarcação do chassi',
    sub: 'Adquirente: chassi que precisa ser remarcado, no veículo que está adquirindo',
    alvo: 'chassi',
    comMotor: false
  }),
  declaracaoRemarcacao({
    id: 'remarcacao-motor',
    titulo: 'Responsabilidade pela remarcação do motor',
    sub: 'Adquirente: motor que precisa ser remarcado, no veículo que está adquirindo',
    alvo: 'motor',
    comMotor: true
  }),
  {
    id: 'acesso-numero-motor',
    grupo: 'Declarações',
    titulo: 'Acesso ao número identificador do motor',
    sub: 'Vistoria: remoção/limpeza de componentes (Portaria DETRAN/RS 475/15)',
    campos: [
      { chave: 'dataVistoria', rotulo: 'Data da vistoria', tipo: 'data' },
      { chave: 'horaVistoria', rotulo: 'Horário da vistoria', tipo: 'hora' },
      {
        chave: 'decisao',
        rotulo: 'Remoção/limpeza de componentes',
        tipo: 'select',
        opcoes: [
          { valor: '', rotulo: 'Deixar em branco (marcar à mão)' },
          { valor: 'IREI', rotulo: 'Irei realizar' },
          { valor: 'NAO_IREI', rotulo: 'Não irei realizar' }
        ]
      }
    ],
    gerar: (ctx) => {
      const { dataVistoria, horaVistoria, decisao } = ctx.extras;
      const marca = (v) => (decisao === v ? 'X' : '&nbsp;&nbsp;');
      const hora = preenchido(horaVistoria) ? e(texto(horaVistoria)) : '____:____';
      return [
        titulo('DECLARAÇÃO SOBRE ACESSO AO NÚMERO IDENTIFICADOR DO MOTOR'),
        par(`${qualificacao(ctx.declarante)}.`),
        par('Proprietário, DECLARO, para os devidos fins, que, durante a vistoria do veículo abaixo identificado:'),
        veiculoLista(ctx.veiculo, ['PLACA', 'CHASSI', 'MODELO', 'RENAVAM']),
        par(`A ser realizada no dia ${e(dataCurta(dataVistoria))}, às ${hora}, (&nbsp;${marca('IREI')}&nbsp;) <strong>irei</strong> / (&nbsp;${marca('NAO_IREI')}&nbsp;) <strong>não irei</strong> realizar a remoção e/ou limpeza de componente(s) que estejam impossibilitando a visualização e acesso ao número identificador do motor do referido veículo.`),
        par('Estou ciente de que a correta visualização do número identificador do motor é essencial para a conclusão da vistoria e assumo total responsabilidade pela decisão acima assinalada conforme <strong>Portaria DETRAN/RS 475/15 POP 08-6.1.5 i)</strong>.'),
        par('Por ser verdade, firmo a presente declaração.'),
        fechamento(`Local: ${e(texto(ctx.local) || '______________________')}, Data: ${dataCurta(ctx.dataISO)}`, 'Assinatura do Proprietário')
      ].join('\n');
    }
  },
  {
    id: 'alteracao-cor',
    grupo: 'Declarações',
    titulo: 'Alteração de cor (mão de obra própria)',
    sub: 'Responsabilidade por pintura em outra cor feita pelo próprio proprietário',
    campos: [
      { chave: 'corAnterior', rotulo: 'Cor anterior', tipo: 'texto', placeholder: 'Em branco usa a cor do cadastro do veículo' },
      { chave: 'corNova', rotulo: 'Nova cor', tipo: 'texto', placeholder: 'Ex.: PRETA' }
    ],
    gerar: (ctx) => {
      const { corAnterior, corNova } = ctx.extras;
      const de = preenchido(corAnterior) ? corAnterior : ctx.veiculo.cor;
      return [
        titulo('DECLARAÇÃO DE RESPONSABILIDADE'),
        par(`${qualificacao(ctx.declarante)}, proprietário(a) do veículo ${veiculoFrase({ ...ctx.veiculo, cor: '' })}, declaro para os devidos fins, que o veículo acima citado sofreu alteração de cor de ${negrito(de)} para ${negrito(corNova)}, sendo o serviço efetuado com mão de obra própria, e fico civil e criminalmente responsável pelas informações prestadas.`),
        fechamento(localData(ctx), 'Declarante')
      ].join('\n');
    }
  },
  declaracaoNaoApresentacao({
    id: 'etiquetas-chapeamento-pintura',
    titulo: 'Etiquetas — chapeamento e pintura (mão de obra própria)',
    sub: 'Não apresentação das etiquetas de identificação após chapeamento e pintura',
    objeto: 'das etiquetas de identificação,',
    motivo: 'sendo que o veículo passou por serviços de chapeamento e pintura com mão de obra própria, sem necessidade de contratação de mão de obra especializada'
  }),
  // Veículo reprovado na vistoria e regularizado com peças que já eram do proprietário (apenas removidas e
  // reinstaladas): dispensa a nota fiscal de procedência. As peças são uma lista (botão + na tela).
  {
    id: 'procedencia-pecas',
    grupo: 'Declarações',
    titulo: 'Responsabilidade pela procedência de peças',
    sub: 'Veículo reprovado na vistoria, regularizado com peças próprias reinstaladas (sem nota fiscal)',
    rotuloDeclarante: 'Proprietário (declarante)',
    campos: [
      { chave: 'pecas', rotulo: 'Peças instaladas', tipo: 'lista', placeholder: 'Ex.: farol dianteiro direito', rotuloAdicionar: 'Adicionar peça' }
    ],
    gerar: (ctx) => {
      const pecas = (ctx.extras.pecas || []).map(texto).filter(Boolean);
      // Sem peças informadas, deixa linhas em branco para preencher à mão
      const itens = pecas.length ? pecas : ['', '', ''];
      const listaPecas = `<div class="veiculo">\n${itens.map((p) => `<div>&bull; ${p ? `<strong>${e(p)}</strong>` : CAMPO_EM_BRANCO + CAMPO_EM_BRANCO}</div>`).join('\n')}\n</div>`;
      const plural = pecas.length !== 1;
      return [
        titulo('DECLARAÇÃO DE RESPONSABILIDADE PELA PROCEDÊNCIA DE PEÇAS'),
        par(`${qualificacao(ctx.declarante)}, proprietário(a) do veículo ${veiculoFrase(ctx.veiculo)}, declaro, para os devidos fins, que o referido veículo, reprovado na vistoria, foi devidamente regularizado mediante a instalação ${plural ? 'das seguintes peças' : 'da seguinte peça'}:`),
        listaPecas,
        par(`Declaro, ainda, que ${plural ? 'as referidas peças já eram de minha propriedade e possuem procedência lícita, tendo sido apenas removidas do veículo e agora reinstaladas' : 'a referida peça já era de minha propriedade e possui procedência lícita, tendo sido apenas removida do veículo e agora reinstalada'}, razão pela qual não há necessidade de apresentação de nota fiscal de procedência.`),
        par('Por ser expressão da verdade, declaro-me civil, administrativa e criminalmente responsável pelas informações aqui prestadas.'),
        fechamento(localData(ctx), 'Proprietário (declarante)')
      ].join('\n');
    }
  },
  declaracaoNaoApresentacao({
    id: 'extravio-etiqueta-ano',
    titulo: 'Perda e extravio da etiqueta do ano (moto)',
    sub: 'Não apresentação da etiqueta de identificação do ano por extravio',
    objeto: 'da etiqueta de identificação do ano',
    motivo: MOTIVO_EXTRAVIO
  }),
  declaracaoNaoApresentacao({
    id: 'extravio-plaqueta',
    titulo: 'Extravio de plaqueta',
    sub: 'Não apresentação da plaqueta de identificação por extravio',
    objeto: 'da plaqueta de identificação',
    motivo: MOTIVO_EXTRAVIO
  }),
  declaracaoNaoApresentacao({
    id: 'extravio-plaqueta-bloco-motor',
    titulo: 'Extravio da plaqueta do bloco do motor',
    sub: 'Não apresentação da plaqueta do bloco do motor por extravio',
    objeto: 'da plaqueta do bloco do motor',
    motivo: MOTIVO_EXTRAVIO
  }),
  declaracaoNaoApresentacao({
    id: 'extravio-tampa-motor',
    titulo: 'Extravio da tampa do motor',
    sub: 'Não apresentação da tampa do motor por extravio',
    objeto: 'da tampa do motor',
    motivo: MOTIVO_EXTRAVIO
  }),

  // Carroceria fabricada antes do CAT (07/05/2002): o proprietário declara a data e a procedência, e fica
  // dispensada a apresentação do CAT/CCT. O mesmo texto pode ir junto no recibo de carroceria.
  {
    id: 'carroceria-anterior-2002',
    grupo: 'Declarações',
    titulo: 'Carroceria fabricada antes de 07/05/2002',
    sub: 'Dispensa de CAT e CCT para carroceria fabricada antes da Portaria DENATRAN nº 27/2002',
    rotuloDeclarante: 'Proprietário (declarante)',
    campos: camposCarroceria,
    gerar: (ctx) => [
      titulo('DECLARAÇÃO'),
      par(`${qualificacao(ctx.declarante)}, proprietário(a) do veículo ${veiculoFrase(ctx.veiculo)}, declaro, para os devidos fins, que a ${descricaoCarroceria(ctx.extras)} instalada no referido veículo ${DISPENSA_CAT}.`),
      par('Declaro, ainda, que a referida carroceria possui procedência lícita, responsabilizando-me civil e criminalmente pelas informações aqui prestadas.'),
      fechamento(localData(ctx), 'Proprietário (declarante)')
    ].join('\n')
  },

  // ── Requerimentos ─────────────────────────────────────────────────────────
  {
    id: 'requerimento-atpv-e',
    grupo: 'Requerimentos',
    titulo: 'Preenchimento da ATPV-e',
    sub: 'Anexo 2 do POP 02 — vendedor, adquirente e valor',
    comprador: true,
    campos: [
      { chave: 'orgaoEmissor', rotulo: 'Órgão emissor do RG', tipo: 'texto', placeholder: 'SSP/RS' },
      { chave: 'valor', rotulo: 'Valor da venda', tipo: 'valor' }
    ],
    gerar: (ctx) => {
      const { declarante: v, comprador: c, veiculo, extras } = ctx;
      const campo = (rotulo, valor) => `<div class="campo"><strong>${rotulo}:</strong> ${valorOuLinha(valor)}</div>`;
      const rg = preenchido(v.rg) ? `, RG nº ${negrito(v.rg)}` : '';
      const orgao = preenchido(extras.orgaoEmissor) ? `, órgão emissor ${negrito(extras.orgaoEmissor)}` : '';
      const cep = formatarCep(c.cep);
      return [
        titulo('ANEXO 2 - REQUERIMENTO DE PREENCHIMENTO DA ATPV-e', true),
        par(`Eu, ${negrito(v.nome)}, ${rotuloDocumento(v.cpfCnpj)} nº ${negrito(formatarCpfCnpj(v.cpfCnpj))}${rg}${orgao}, requeiro ao DETRAN/RS, o preenchimento da ATPV-e, relativo ao veículo placa ${negrito((veiculo.placa || '').toUpperCase())}, chassi ${negrito((veiculo.chassi || '').toUpperCase())}.`),
        '<p class="secao">PROPRIETÁRIO VENDEDOR</p>',
        campo('E-MAIL', v.email),
        '<p class="secao">IDENTIFICAÇÃO DO ADQUIRENTE</p>',
        campo('CPF/CNPJ', formatarCpfCnpj(c.cpfCnpj)),
        campo('NOME', c.nome),
        campo('E-MAIL', c.email),
        '<p class="secao">ENDEREÇO DO ADQUIRENTE</p>',
        campo('CEP', cep),
        campo('UF', c.uf),
        campo('MUNICÍPIO', c.cidade),
        campo('LOGRADOURO', c.logradouro),
        campo('Nº', c.numero),
        campo('COMPLEMENTO', c.complemento),
        campo('BAIRRO', c.bairro),
        `<p class="secao">VALOR: ${preenchido(extras.valor) ? e(formatarMoeda(extras.valor)) : 'R$ <span class="branco"></span>'}</p>`,
        '<p class="caixa"><strong>DECLARO, sob as penas da lei e sem prejuízo de demais sanções administrativas/cíveis/criminais, que os documentos entregues ao DETRAN/RS para o serviço requerido são todos autênticos e, quando cópias, condizem com o original.</strong></p>',
        fechamento(`Data: ${dataCurta(ctx.dataISO)}`, 'ASSINATURA DO VENDEDOR/REPRESENTANTE LEGAL')
      ].join('\n');
    }
  },
  // Requerimentos com o formulário do DETRAN/RS (o mesmo da Nexus CRVA): aqui se escolhe o cliente e o veículo,
  // e "Gerar documento" abre o formulário numa nova aba (RequerimentoDetranPage), onde se marca e imprime.
  {
    id: 'requerimento-servico',
    grupo: 'Requerimentos',
    titulo: 'Requerimento de serviço',
    sub: 'Anexo 1 — requerimento de serviço com declaração de autenticidade de documentos',
    rotuloDeclarante: 'Requerente (adquirente ou proprietário)',
    formularioDetran: 'SERVICO_DECLARACAO',
    campos: []
  },
  {
    id: 'requerimento-primeiro-emplacamento',
    grupo: 'Requerimentos',
    titulo: 'Primeiro emplacamento',
    sub: 'Anexo 1 — requerimento de serviço para o processo de primeiro emplacamento (veículo 0 km)',
    rotuloDeclarante: 'Requerente (adquirente)',
    formularioDetran: 'PRIMEIRO_EMPLACAMENTO',
    campos: []
  },
  {
    id: 'requerimento-segunda-via-crv',
    grupo: 'Requerimentos',
    titulo: '2ª via ou validação de CRV',
    sub: 'Anexo 1 do POP 10 — solicitação de 2ª via ou validação do CRV',
    campos: [
      {
        chave: 'tipo',
        rotulo: 'Requerimento',
        tipo: 'select',
        opcoes: [
          { valor: 'SEGUNDA_VIA', rotulo: 'Solicitação de 2ª via de CRV' },
          { valor: 'VALIDACAO', rotulo: 'Validação do CRV' },
          { valor: '', rotulo: 'Deixar em branco (marcar à mão)' }
        ],
        padrao: 'SEGUNDA_VIA'
      },
      { chave: 'numeroCrv', rotulo: 'Nº do CRV (validação)', tipo: 'texto' },
      { chave: 'dataCrvAnterior', rotulo: 'Data de emissão do CRV anterior', tipo: 'data' },
      {
        chave: 'justificativa',
        rotulo: 'Justificativa da não apresentação do CRV anterior',
        tipo: 'select',
        opcoes: [{ valor: '', rotulo: 'Deixar em branco (marcar à mão)' }, ...JUSTIFICATIVAS_CRV.map(({ valor, rotulo }) => ({ valor, rotulo }))]
      }
    ],
    gerar: (ctx) => {
      const { tipo, numeroCrv, dataCrvAnterior, justificativa } = ctx.extras;
      const { declarante: p, veiculo: v } = ctx;
      const validacao = `Validação do CRV número ${preenchido(numeroCrv) ? e(texto(numeroCrv)) : '<span class="branco"></span>'}, para efeitos de transferência de propriedade do veículo.`;
      return [
        titulo('ANEXO 1 – REQUERIMENTO PARA SOLICITAÇÃO DE 2ª VIA DE CRV OU VALIDAÇÃO DE CRV', true),
        par(`Eu, ${negrito(p.nome)}, identidade nº ${negrito(p.rg)}, ${rotuloDocumento(p.cpfCnpj)} nº ${negrito(formatarCpfCnpj(p.cpfCnpj))}, ${e(`residente e domiciliado(a) em ${enderecoCompleto(p)}`)}, proprietário do veículo marca ${negrito(v.marcaModelo)}, placas ${negrito((v.placa || '').toUpperCase())}, chassi ${negrito((v.chassi || '').toUpperCase())}, requeiro:`),
        `<div class="opcao">(&nbsp;${tipo === 'SEGUNDA_VIA' ? 'X' : '&nbsp;&nbsp;'}&nbsp;) Solicitação de 2ª via de CRV;</div>`,
        `<div class="opcao">(&nbsp;${tipo === 'VALIDACAO' ? 'X' : '&nbsp;&nbsp;'}&nbsp;) ${validacao}</div>`,
        par(`Justificativa da não apresentação do CRV anterior válido emitido pelo DETRAN/RS em ${e(dataCurta(dataCrvAnterior))}:`),
        ...JUSTIFICATIVAS_CRV.map((j) => opcao(justificativa === j.valor, j.rotulo)),
        par('O proprietário declarante responsabiliza-se civil e criminalmente pela veracidade das informações prestadas.'),
        fechamento(localData(ctx), 'Assinatura do Proprietário/Arrendatário/Procurador com firma reconhecida por autenticidade no caso do não-comparecimento do signatário ao Centro')
      ].join('\n');
    }
  },
  {
    id: 'requerimento-baixa-definitiva',
    grupo: 'Requerimentos',
    titulo: 'Baixa definitiva de veículo',
    sub: 'Anexo 1 do POP 25 — Resolução CONTRAN nº 967/22',
    campos: [
      campoEscolha('motivo', 'Motivo da baixa', MOTIVOS_BAIXA),
      { chave: 'perdidos', rotulo: 'Documentos/elementos perdidos ou extraviados', tipo: 'checks', opcoes: PERDIDOS_BAIXA }
    ],
    gerar: (ctx) => {
      const { declarante: p, veiculo: v, extras } = ctx;
      const perdidos = extras.perdidos || [];
      return [
        titulo('ANEXO 1 - REQUERIMENTO PARA BAIXA DEFINITIVA (CONFORME RESOLUÇÃO CONTRAN Nº 967/22)', true),
        par(`Eu, ${negrito(p.nome)}, ${portador(p)}, proprietário/responsável do veículo placa ${negrito((v.placa || '').toUpperCase())}, chassi ${negrito((v.chassi || '').toUpperCase())}, marca/modelo ${negrito(v.marcaModelo)}, requeiro a BAIXA DEFINITIVA do mesmo, sob pena de responsabilidade civil e criminal, devido tratar-se de:`),
        ...MOTIVOS_BAIXA.map((m) => opcao(extras.motivo === m.valor, m.rotulo)),
        par('Bem como, declaro que perdi/extraviei os seguintes documentos/elementos:'),
        ...PERDIDOS_BAIXA.map((m) => opcao(perdidos.includes(m.valor), m.rotulo)),
        fechamento(localData(ctx), 'Requerente', '(Reconhecer firma do proprietário ou seu representante, por autenticidade em caso de não comparecimento ao CRVA, ou assinado com assinatura eletrônica, conforme POP 47)')
      ].join('\n');
    }
  },

  // ── Recibos ───────────────────────────────────────────────────────────────
  // veiculoOrigem: a tela mostra o quadro do veículo de onde a peça saiu (importar CRLV ou buscar no cadastro)
  {
    id: 'recibo-motor',
    grupo: 'Recibos',
    titulo: 'Recibo de compra e venda de motor',
    sub: 'Vendedor e comprador, nº do motor, veículo de onde saiu, veículo em que é instalado e valor',
    comprador: true,
    rotuloDeclarante: 'Vendedor',
    rotuloVeiculo: 'Veículo em que o motor será instalado',
    veiculoOrigem: 'Veículo de onde o motor saiu',
    campos: [
      { chave: 'numeroMotor', rotulo: 'Nº do motor', tipo: 'texto', placeholder: 'Número gravado no motor' },
      campoValorRecibo
    ],
    gerar: (ctx) => [
      titulo('RECIBO DE COMPRA E VENDA DE MOTOR'),
      par(`${recebimento(ctx)}, referente à venda do motor nº ${negrito((ctx.extras.numeroMotor || '').toUpperCase())}, de minha propriedade, retirado do veículo ${veiculoFrase(ctx.veiculoOrigem || {})}.`),
      par(`O referido motor será instalado no veículo ${veiculoFrase(ctx.veiculo)}.`),
      par(PROCEDENCIA_RECIBO),
      fechamento(localData(ctx), 'Vendedor')
    ].join('\n')
  },
  {
    id: 'recibo-carroceria',
    grupo: 'Recibos',
    titulo: 'Recibo de compra e venda de carroceria',
    sub: 'Vendedor e comprador, tipo da carroceria, veículo de onde saiu, veículo em que é instalada e valor',
    comprador: true,
    rotuloDeclarante: 'Vendedor',
    rotuloVeiculo: 'Veículo em que a carroceria será instalada',
    veiculoOrigem: 'Veículo de onde a carroceria saiu',
    campos: [
      ...camposCarroceria,
      campoValorRecibo,
      {
        chave: 'declaracoes',
        rotulo: 'Declaração de dispensa de CAT/CCT',
        tipo: 'checks',
        opcoes: [{ valor: ANTERIOR_CAT, rotulo: 'Carroceria fabricada antes de 07/05/2002 (inclui a declaração de que não há CAT/CCT)' }]
      }
    ],
    gerar: (ctx) => {
      const { extras } = ctx;
      return [
        titulo('RECIBO DE COMPRA E VENDA DE CARROCERIA'),
        par(`${recebimento(ctx)}, referente à venda de uma ${descricaoCarroceria(extras)}, de minha propriedade, retirada do veículo ${veiculoFrase(ctx.veiculoOrigem || {})}.`),
        par(`A referida carroceria será instalada no veículo ${veiculoFrase(ctx.veiculo)}.`),
        (extras.declaracoes || []).includes(ANTERIOR_CAT) ? par(`Declaro, também, que a referida carroceria ${DISPENSA_CAT}.`) : '',
        par(PROCEDENCIA_RECIBO),
        fechamento(localData(ctx), 'Vendedor')
      ].filter(Boolean).join('\n');
    }
  }
];

export const GRUPOS = ['Declarações', 'Requerimentos', 'Recibos'];

export const MODELOS_POR_ID = MODELOS.reduce((acc, m) => ({ ...acc, [m.id]: m }), {});

// Valores iniciais dos campos extras de um modelo
export function extrasIniciais(modelo) {
  return modelo.campos.reduce((acc, c) => {
    if (c.tipo === 'checks') acc[c.chave] = [];
    else if (c.tipo === 'valor') acc[c.chave] = null;
    else if (c.tipo === 'lista') acc[c.chave] = [''];
    else acc[c.chave] = c.padrao !== undefined ? c.padrao : '';
    return acc;
  }, {});
}

// Substitui {{TOKEN}} pelo valor correspondente — mesmo esquema simples usado em Procuração/Contrato.
function substituirTokens(texto, valores) {
  let resultado = texto;
  Object.entries(valores).forEach(([chave, valor]) => {
    resultado = resultado.split(`{{${chave}}}`).join(escapeHtml(valor || '')); // dado de cliente/veículo entra sempre escapado
  });
  return resultado;
}

// Tokens comuns a qualquer modelo (dados específicos de "extras" de cada modelo não viram token,
// já que variam muito de documento pra documento — mantemos só o que é sempre igual).
export function tokensComuns(ctx) {
  return {
    DECLARANTE: ctx.declarante?.nome || '',
    COMPRADOR: ctx.comprador?.nome || '',
    VEICULO_PLACA: (ctx.veiculo?.placa || '').toUpperCase(),
    VEICULO_CHASSI: (ctx.veiculo?.chassi || '').toUpperCase(),
    VEICULO_MARCA_MODELO: ctx.veiculo?.marcaModelo || '',
    LOCAL: ctx.local || '',
    DATA: dataCurta(ctx.dataISO)
  };
}

// ctx: { declarante, comprador, veiculo, extras, local, dataISO }
// textoCustomizado: texto salvo pela empresa em Configurações para este modelo (opcional) —
// quando presente, substitui o HTML padrão gerado por modelo.gerar(ctx).
export function gerarDocumentoHtml(modelo, ctx, textoCustomizado) {
  // Requerimentos com formulário próprio (formularioDetran) não geram HTML aqui
  if (!modelo.gerar) return '';
  if (textoCustomizado) {
    return substituirTokens(textoCustomizado, tokensComuns(ctx));
  }
  return modelo.gerar(ctx);
}
